import { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import LauncherContainerWrapper from '../launcher/container/LauncherContainerWrapper';
import { withBrowserSizeContext } from '../containers/withBrowserSizeContext';
import WidgetWindow from './WidgetWindow';
import { FocusScope } from '@react-aria/focus';
import { AccessibilityContext } from 'conversations-visitor-message-history/accessibility/AccessibilityContext';
import { getUsePillLauncher, getIsUngatedForUpdatedChatLauncherExperience } from '../widget-data/selectors/widgetDataSelectors';
import { handleDragHandlePositionChange } from '../post-message/handleDragHandlePositionChange';
import WidgetWelcomePage from './WidgetWelcomePage';
import InitialPageContentWrapper from 'conversations-visitor-experience-components/visitor-widget/components/InitialPageContentWrapper';
import { CustomerAgentHoverProvider, useCustomerAgentHover } from 'conversations-visitor-experience-components/contexts/CustomerAgentHoverContext';
import { getInitialMessageForWelcomePage } from '../selectors/widgetDataSelectors/getInitialMessageForWelcomePage';
import { useSideDockContext } from '../side-dock/components/SideDockWidgetWrapper';
import { hasPersistedThreads } from '../threads/selectors/hasPersistedThreads';
import { getRecommendedQuestionsForAgent } from '../widget-data/selectors/getRecommendedQuestionsForAgent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ApplicationLayoutContent({
  inline,
  isOpen,
  mobile,
  showInitialMessageBubble,
  speechPocActive,
  toggleOpen,
  trackUserInteraction,
  widgetLocation,
  isAIChatBot,
  onSetMessageTextExternally,
  showDockedPanel
}) {
  const [animationsFinished, setAnimationsFinished] = useState(true);
  const [shouldRenderDialog, setShouldRenderDialog] = useState(isOpen);
  const accessibilityContext = useContext(AccessibilityContext);
  const usePillLauncher = useSelector(getUsePillLauncher);
  const {
    onCloseAnimationComplete
  } = useSideDockContext();
  const isUngatedForUpdatedLauncher = useSelector(getIsUngatedForUpdatedChatLauncherExperience);
  const hasExistingThreads = useSelector(hasPersistedThreads);
  const recommendedQuestionsForAgent = useSelector(getRecommendedQuestionsForAgent);
  const hasPrompts = recommendedQuestionsForAgent.length > 0;
  const {
    isHoverActive: isHoveringPillLauncher,
    resetHoverState
  } = useCustomerAgentHover();
  const shouldHideLauncherForExpandedContainer = usePillLauncher && isUngatedForUpdatedLauncher && isHoveringPillLauncher && !hasExistingThreads && hasPrompts;
  useEffect(() => {
    if (isOpen) {
      return;
    }
    if (shouldHideLauncherForExpandedContainer) {
      handleDragHandlePositionChange({
        dragHandleState: 'hidden'
      });
      return;
    }
    const dragHandleState = usePillLauncher ? 'pill-launcher' : 'icon-launcher';
    handleDragHandlePositionChange({
      dragHandleState
    });
  }, [usePillLauncher, shouldHideLauncherForExpandedContainer, isOpen]);
  const openWidget = () => {
    setAnimationsFinished(false);
    trackUserInteraction();
    toggleOpen({
      isOpened: true,
      isUser: true,
      openedFrom: usePillLauncher ? 'pill launcher' : 'icon launcher'
    });
    accessibilityContext.setShouldTrapFocus(true);
  };
  const closeWidget = () => {
    setAnimationsFinished(false);
    resetHoverState();
    trackUserInteraction();
    toggleOpen({
      isOpened: false,
      isUser: true
    });
  };
  const closeWidgetWelcomePage = () => {
    setAnimationsFinished(true);
    trackUserInteraction();
  };
  const onOpenAnimationStarted = () => {
    setAnimationsFinished(false);
    setShouldRenderDialog(true);
    handleDragHandlePositionChange({
      dragHandleState: 'hidden'
    });
  };
  const onOpenAnimationFinished = () => {
    setAnimationsFinished(true);
    accessibilityContext.setShouldTrapFocus(true);
    handleDragHandlePositionChange({
      dragHandleState: showDockedPanel ? 'hidden' : 'widget-open'
    });
  };
  const onCloseAnimationStarted = () => {
    setAnimationsFinished(false);
    handleDragHandlePositionChange({
      dragHandleState: 'hidden'
    });
  };
  const onCloseAnimationFinished = () => {
    setAnimationsFinished(true);
    setShouldRenderDialog(false);
    accessibilityContext.setShouldTrapFocus(false);
    handleDragHandlePositionChange({
      dragHandleState: usePillLauncher ? 'pill-launcher' : 'icon-launcher'
    });
    if (showDockedPanel) {
      onCloseAnimationComplete();
    }
  };
  const onLauncherAnimationStart = () => {
    handleDragHandlePositionChange({
      dragHandleState: 'hidden'
    });
  };
  const onLauncherAnimationFinished = () => {
    handleDragHandlePositionChange({
      dragHandleState: isHoveringPillLauncher ? 'hidden' : 'pill-launcher'
    });
  };
  const launcherHidden = isOpen && mobile || inline || isOpen && usePillLauncher || isOpen && showDockedPanel && animationsFinished;
  const showContent = !usePillLauncher || usePillLauncher && animationsFinished;
  const initialMessage = useSelector(getInitialMessageForWelcomePage);
  return /*#__PURE__*/_jsx(AccessibilityContext.Consumer, {
    children: ({
      shouldTrapFocus
    }) => /*#__PURE__*/_jsxs(FocusScope, {
      contain: shouldTrapFocus,
      autoFocus: false,
      children: [/*#__PURE__*/_jsx(WidgetWindow, {
        inline: inline,
        isOpen: isOpen || inline,
        shouldRenderDialog: shouldRenderDialog,
        mobile: mobile,
        widgetLocation: widgetLocation,
        onOpenAnimationStarted: onOpenAnimationStarted,
        onCloseAnimationFinished: onCloseAnimationFinished,
        onOpenAnimationFinished: onOpenAnimationFinished,
        onCloseAnimationStarted: onCloseAnimationStarted,
        onClose: closeWidget,
        speechPocActive: speechPocActive,
        showPillLauncher: usePillLauncher,
        showDockedPanel: showDockedPanel,
        onSetMessageTextExternally: onSetMessageTextExternally
      }), showContent && (!isOpen || !usePillLauncher) && /*#__PURE__*/_jsxs(InitialPageContentWrapper, {
        isMobile: mobile,
        widgetLocation: widgetLocation,
        showPillLauncher: usePillLauncher,
        shouldTrapFocus: isAIChatBot && usePillLauncher && !mobile,
        usePillLauncher: usePillLauncher,
        isUngatedForLauncherExperience: isUngatedForUpdatedLauncher,
        children: [showInitialMessageBubble && !isOpen && animationsFinished && /*#__PURE__*/_jsx(WidgetWelcomePage, {
          onClose: closeWidgetWelcomePage,
          initialMessage: initialMessage,
          isMobile: mobile,
          showPillLauncher: usePillLauncher
        }), !launcherHidden && !shouldHideLauncherForExpandedContainer && /*#__PURE__*/_jsx(LauncherContainerWrapper, {
          onClose: closeWidget,
          onOpen: openWidget,
          showPillLauncher: usePillLauncher,
          onLauncherAnimationStart: onLauncherAnimationStart,
          onLauncherAnimationFinished: onLauncherAnimationFinished,
          disableHoverExpansion: isUngatedForUpdatedLauncher && (hasExistingThreads || !hasPrompts)
        })]
      })]
    })
  });
}
ApplicationLayoutContent.displayName = 'ApplicationLayoutContent';
export function ApplicationLayout(props) {
  const usePillLauncher = useSelector(getUsePillLauncher);
  return /*#__PURE__*/_jsx(CustomerAgentHoverProvider, {
    enableHoverTracking: usePillLauncher,
    isUngatedForLauncherExperience: props.isUngatedForLauncherExperience,
    children: /*#__PURE__*/_jsx(ApplicationLayoutContent, Object.assign({}, props))
  });
}
ApplicationLayout.displayName = 'ApplicationLayout';
export default withBrowserSizeContext(ApplicationLayout);